import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Slider,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  IconButton,
  Paper,
  FormGroup,
  Skeleton,
  Stack,
  Button,
  Drawer
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import { ProductFilterOptions, sortOptions } from '../api/api';
import { Category } from '../types/api';
import { useQuery } from '@tanstack/react-query';
import { productsApi } from '../api/api';

// Add this type to define the numeric ranges for filters
interface NumericFilterRange {
  min: number;
  max: number;
  value: [number, number];
}

interface FilterProps {
  filters: ProductFilterOptions;
  onFilterChange: (filters: ProductFilterOptions) => void;
  categories?: Category[];
  currentCategoryId?: number;
  isLoading?: boolean;
}

// Use this type for the product filters from API response
interface ProductFiltersType {
  price?: {
    minValue: number;
    maxValue: number;
  };
  attributes?: {
    [key: string]: Array<{
      id: string;
      title: string;
      titleTranslated?: string;
      productCount: number;
    }>;
  };
  options?: Record<string, any>;
  inventory?: any[];
  onsale?: any[];
}

// Define a complete response type to match the Ecwid API response
interface ProductFiltersResponse {
  productCount: number;
  filters: ProductFiltersType;
}

interface ActiveFiltersProps {
  filters: ProductFilterOptions;
  onClearFilter: (key: string, value?: string) => void;
  onClearAll: () => void;
  productFilters?: ProductFiltersType;
  categories?: Category[];
  numericFilters?: Record<string, NumericFilterRange>;
}

// Helper function to get readable labels for filter values
const getFilterLabel = (
  key: string,
  value: any,
  productFilters?: ProductFiltersType,
  categories?: Category[],
  numericFilters?: Record<string, NumericFilterRange>
): string => {
  // Special case for category
  if (key === 'category' && categories) {
    const category = categories.find(cat => cat.id === value);
    return category ? category.name : `Category ${value}`;
  }
  
  // Special case for sort
  if (key === 'sortBy') {
    const sortOption = sortOptions.find(opt => opt.value === value);
    return sortOption ? sortOption.label : value;
  }
  
  // Special case for boolean filters
  if (key === 'inStock' && value === true) return 'In Stock Only';
  if (key === 'onSale' && value === true) return 'On Sale Only';
  
  // Special case for price ranges
  if (key === 'priceFrom') return `Min Price: $${value}`;
  if (key === 'priceTo') return `Max Price: $${value}`;
  
  // Special case for attributes that are in numeric filters
  if (key.startsWith('attribute_') && numericFilters) {
    const attributeName = key.replace('attribute_', '');
    if (numericFilters[attributeName]) {
      const range = numericFilters[attributeName];
      if (Array.isArray(value)) {
        return `${attributeName}: ${value[0]} - ${value[1]}`;
      }
      return `${attributeName}: ${value}`;
    }
  }
  
  // Fallback for other filters
  return `${key.replace('_', ' ')}: ${value}`;
};

const ActiveFilters: React.FC<ActiveFiltersProps> = ({ 
  filters, 
  onClearFilter, 
  onClearAll,
  productFilters,
  categories,
  numericFilters
}) => {
  const activeFilters = Object.entries(filters).filter(([key, value]) => {
    // Skip offset, limit, and sortBy from active filters display
    if (['offset', 'limit', 'sortBy', 'keyword', 'category'].includes(key)) return false;
    
    // Include non-empty values
    return value !== undefined && value !== null && value !== '';
  });
  
  if (activeFilters.length === 0) return null;
  
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Active Filters
        </Typography>
        {activeFilters.length > 0 && (
          <Button 
            size="small" 
            onClick={onClearAll}
            sx={{ textTransform: 'none' }}
          >
            Clear All
          </Button>
        )}
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {activeFilters.map(([key, value]) => (
          <Chip
            key={key}
            label={getFilterLabel(key, value, productFilters, categories, numericFilters)}
            onDelete={() => onClearFilter(key)}
            size="small"
            sx={{ m: 0.5 }}
          />
        ))}
      </Stack>
    </Box>
  );
};

const ProductFilters: React.FC<FilterProps> = ({
  filters,
  onFilterChange,
  categories,
  currentCategoryId,
  isLoading = false
}) => {
  // Track expanded accordion sections
  const [expanded, setExpanded] = useState<string | false>('sort');
  
  // Track price range
  const [priceRange, setPriceRange] = useState<[number, number]>([
    filters.priceFrom || 0,
    filters.priceTo || 100
  ]);
  
  // Track numeric attribute ranges (Number of Servings, Price per Serving)
  const [numericFilters, setNumericFilters] = useState<Record<string, NumericFilterRange>>({
    'Number of Servings': {
      min: 0,
      max: 50,
      value: [0, 50]
    },
    'Price per Serving': {
      min: 0,
      max: 10,
      value: [0, 10]
    }
  });
  
  // Mobile filters state
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  
  // Fetch product filters from API
  const { data: productFiltersData } = useQuery<ProductFiltersResponse>({
    queryKey: ['productFilters', filters.category],
    queryFn: () => productsApi.getProductFilters(filters.category ? [filters.category] : undefined),
    enabled: !isLoading,
  });
  
  // Update price range when filters change
  useEffect(() => {
    if (filters.priceFrom !== undefined || filters.priceTo !== undefined) {
      setPriceRange([
        filters.priceFrom || 0,
        filters.priceTo || 100
      ]);
    }
  }, [filters.priceFrom, filters.priceTo]);
  
  // Update numeric filters min/max based on API response
  useEffect(() => {
    if (productFiltersData?.filters?.attributes) {
      // Update Number of Servings if available
      if (productFiltersData.filters.attributes['Number of Servings']) {
        const values = productFiltersData.filters.attributes['Number of Servings']
          .map(item => parseFloat(item.id))
          .filter(value => !isNaN(value));
        
        if (values.length > 0) {
          const min = Math.min(...values);
          const max = Math.max(...values);
          
          setNumericFilters(prev => ({
            ...prev,
            'Number of Servings': {
              ...prev['Number of Servings'],
              min,
              max,
              value: [
                Math.max(min, prev['Number of Servings'].value[0]),
                Math.min(max, prev['Number of Servings'].value[1])
              ]
            }
          }));
        }
      }
      
      // Update Price per Serving if available
      if (productFiltersData.filters.attributes['Price per Serving']) {
        const values = productFiltersData.filters.attributes['Price per Serving']
          .map(item => parseFloat(item.id))
          .filter(value => !isNaN(value));
        
        if (values.length > 0) {
          const min = Math.min(...values);
          const max = Math.max(...values);
          
          setNumericFilters(prev => ({
            ...prev,
            'Price per Serving': {
              ...prev['Price per Serving'],
              min,
              max,
              value: [
                Math.max(min, prev['Price per Serving'].value[0]),
                Math.min(max, prev['Price per Serving'].value[1])
              ]
            }
          }));
        }
      }
    }
  }, [productFiltersData]);
  
  // Load filters for the current category
  const loadFilters = async () => {
    try {
      // This is handled by the React Query hook above
    } catch (error) {
      console.error('Error loading filters:', error);
    }
  };
  
  useEffect(() => {
    loadFilters();
  }, [currentCategoryId]);
  
  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };
  
  const handlePriceChange = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      setPriceRange(newValue as [number, number]);
    }
  };
  
  const handlePriceChangeCommitted = (event: React.SyntheticEvent | Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      onFilterChange({
        ...filters,
        priceFrom: newValue[0],
        priceTo: newValue[1]
      });
    }
  };
  
  // Handle numeric attribute filter change
  const handleNumericFilterChange = (attributeName: string) => (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      setNumericFilters(prev => ({
        ...prev,
        [attributeName]: {
          ...prev[attributeName],
          value: newValue as [number, number]
        }
      }));
    }
  };
  
  // Handle numeric attribute filter change committed - apply the filter
  const handleNumericFilterChangeCommitted = (attributeName: string) => (event: React.SyntheticEvent | Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      // Update attribute filter
      const attributeKey = `attribute_${attributeName}`;
      const updatedFilters = {
        ...filters,
      } as ProductFilterOptions;
      
      // Safely add the attribute filter
      (updatedFilters as any)[attributeKey] = newValue;
      
      onFilterChange(updatedFilters);
    }
  };
  
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    onFilterChange({
      ...filters,
      [name]: checked
    });
  };
  
  const handleSortChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({
      ...filters,
      sortBy: event.target.value
    });
  };
  
  const handleAttributeChange = (attributeId: number, valueId: string, checked: boolean) => {
    // Get current attributes or initialize empty object
    const currentAttributes = filters.attributes ? { ...filters.attributes } : {};
    
    // Get current values for this attribute
    let attributeValues = currentAttributes[attributeId];
    
    if (checked) {
      // Add the value if checked
      if (!attributeValues) {
        attributeValues = valueId;
      } else {
        attributeValues = `${attributeValues},${valueId}`;
      }
    } else {
      // Remove the value if unchecked
      if (attributeValues) {
        const values = attributeValues.split(',');
        attributeValues = values.filter(v => v !== valueId).join(',');
        
        // If no values left, remove the attribute
        if (!attributeValues) {
          delete currentAttributes[attributeId];
        }
      }
    }
    
    // Update the attribute
    if (attributeValues) {
      currentAttributes[attributeId] = attributeValues;
    }
    
    onFilterChange({
      ...filters,
      attributes: Object.keys(currentAttributes).length > 0 ? currentAttributes : undefined
    });
  };
  
  const handleOptionChange = (optionName: string, optionValue: string, checked: boolean) => {
    // Get current options or initialize empty object
    const currentOptions = filters.options ? { ...filters.options } : {};
    
    if (checked) {
      // Add the option
      currentOptions[optionName] = optionValue;
    } else {
      // Remove the option
      delete currentOptions[optionName];
    }
    
    onFilterChange({
      ...filters,
      options: Object.keys(currentOptions).length > 0 ? currentOptions : undefined
    });
  };
  
  const handleClearFilter = (key: string, nestedKey?: string) => {
    const newFilters = { ...filters } as any;
    
    // Handle special case for nested attributes
    if (key === 'attributes' && nestedKey && newFilters.attributes) {
      const attributes = { ...newFilters.attributes };
      delete attributes[nestedKey];
      newFilters.attributes = Object.keys(attributes).length > 0 ? attributes : undefined;
    }
    // Handle special case for numeric attribute filters
    else if (key.startsWith('attribute_')) {
      const attributeName = key.replace('attribute_', '');
      
      // Reset the numeric filter to its full range
      if (numericFilters[attributeName]) {
        const { min, max } = numericFilters[attributeName];
        setNumericFilters(prev => ({
          ...prev,
          [attributeName]: {
            ...prev[attributeName],
            value: [min, max]
          }
        }));
      }
      
      // Clear the filter
      delete newFilters[key];
    }
    // Handle normal filters
    else {
      delete newFilters[key];
    }
    
    onFilterChange(newFilters);
  };
  
  const handleClearAll = () => {
    // Reset to default filters, keeping only category, keyword, limit, and offset
    const { category, keyword, limit, offset, sortBy } = filters;
    onFilterChange({ category, keyword, limit, offset, sortBy });
    
    // Reset numeric filters to full ranges
    const resetNumericFilters = { ...numericFilters };
    Object.keys(resetNumericFilters).forEach(key => {
      const { min, max } = resetNumericFilters[key];
      resetNumericFilters[key].value = [min, max];
    });
    setNumericFilters(resetNumericFilters);
  };
  
  const toggleMobileFilters = () => {
    setMobileFiltersOpen(!mobileFiltersOpen);
  };
  
  const renderFilters = () => (
    <Box>
      {/* Active filters */}
      <ActiveFilters 
        filters={filters} 
        onClearFilter={handleClearFilter} 
        onClearAll={handleClearAll}
        productFilters={productFiltersData?.filters}
        categories={categories}
        numericFilters={numericFilters}
      />
      
      {/* Sort options */}
      <Accordion 
        expanded={expanded === 'sort'} 
        onChange={handleAccordionChange('sort')}
        elevation={0}
        disableGutters
        sx={{ 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          mb: 2,
          '&::before': { display: 'none' },
          '&.Mui-expanded': {
            margin: 0,
            mb: 2
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ minHeight: 48 }}
        >
          <Typography>Sort By</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth size="small">
            <Select
              value={filters.sortBy || 'NAME_ASC'}
              onChange={handleSortChange as any}
              displayEmpty
              MenuProps={{ 
                PaperProps: { 
                  sx: { maxHeight: 300 } 
                } 
              }}
            >
              {sortOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </AccordionDetails>
      </Accordion>
      
      {/* Price range */}
      <Accordion 
        expanded={expanded === 'price'} 
        onChange={handleAccordionChange('price')}
        elevation={0}
        disableGutters
        sx={{ 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          mb: 2,
          '&::before': { display: 'none' },
          '&.Mui-expanded': {
            margin: 0,
            mb: 2
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ minHeight: 48 }}
        >
          <Typography>Price Range</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ px: 1, mt: 1 }}>
            <Slider
              value={priceRange}
              onChange={handlePriceChange}
              onChangeCommitted={handlePriceChangeCommitted}
              valueLabelDisplay="auto"
              min={0}
              max={100}
              step={1}
              valueLabelFormat={(value) => `$${value}`}
              sx={{ mt: 3 }}
            />
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              mt: 2 
            }}>
              <TextField
                label="Min"
                variant="outlined"
                size="small"
                value={priceRange[0]}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  readOnly: true
                }}
                sx={{ width: '45%' }}
              />
              <TextField
                label="Max"
                variant="outlined"
                size="small"
                value={priceRange[1]}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  readOnly: true
                }}
                sx={{ width: '45%' }}
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* Number of Servings filter */}
      <Accordion 
        expanded={expanded === 'servings'} 
        onChange={handleAccordionChange('servings')}
        elevation={0}
        disableGutters
        sx={{ 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          mb: 2,
          '&::before': { display: 'none' },
          '&.Mui-expanded': {
            margin: 0,
            mb: 2
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ minHeight: 48 }}
        >
          <Typography>Number of Servings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ px: 1, mt: 1 }}>
            <Slider
              value={numericFilters['Number of Servings'].value}
              onChange={handleNumericFilterChange('Number of Servings')}
              onChangeCommitted={handleNumericFilterChangeCommitted('Number of Servings')}
              valueLabelDisplay="auto"
              min={numericFilters['Number of Servings'].min}
              max={numericFilters['Number of Servings'].max}
              step={1}
              sx={{ mt: 3 }}
            />
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              mt: 2 
            }}>
              <TextField
                label="Min"
                variant="outlined"
                size="small"
                value={numericFilters['Number of Servings'].value[0]}
                InputProps={{
                  readOnly: true
                }}
                sx={{ width: '45%' }}
              />
              <TextField
                label="Max"
                variant="outlined"
                size="small"
                value={numericFilters['Number of Servings'].value[1]}
                InputProps={{
                  readOnly: true
                }}
                sx={{ width: '45%' }}
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* Price per Serving filter */}
      <Accordion 
        expanded={expanded === 'pricePerServing'} 
        onChange={handleAccordionChange('pricePerServing')}
        elevation={0}
        disableGutters
        sx={{ 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          mb: 2,
          '&::before': { display: 'none' },
          '&.Mui-expanded': {
            margin: 0,
            mb: 2
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ minHeight: 48 }}
        >
          <Typography>Price per Serving</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ px: 1, mt: 1 }}>
            <Slider
              value={numericFilters['Price per Serving'].value}
              onChange={handleNumericFilterChange('Price per Serving')}
              onChangeCommitted={handleNumericFilterChangeCommitted('Price per Serving')}
              valueLabelDisplay="auto"
              min={numericFilters['Price per Serving'].min}
              max={numericFilters['Price per Serving'].max}
              step={0.1}
              valueLabelFormat={(value) => `$${value.toFixed(2)}`}
              sx={{ mt: 3 }}
            />
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              mt: 2 
            }}>
              <TextField
                label="Min"
                variant="outlined"
                size="small"
                value={`$${numericFilters['Price per Serving'].value[0].toFixed(2)}`}
                InputProps={{
                  readOnly: true
                }}
                sx={{ width: '45%' }}
              />
              <TextField
                label="Max"
                variant="outlined"
                size="small"
                value={`$${numericFilters['Price per Serving'].value[1].toFixed(2)}`}
                InputProps={{
                  readOnly: true
                }}
                sx={{ width: '45%' }}
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* Stock and sale status */}
      <Accordion 
        expanded={expanded === 'stock'} 
        onChange={handleAccordionChange('stock')}
        elevation={0}
        disableGutters
        sx={{ 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          mb: 2,
          '&::before': { display: 'none' },
          '&.Mui-expanded': {
            margin: 0,
            mb: 2
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ minHeight: 48 }}
        >
          <Typography>Availability</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormGroup>
            <FormControlLabel 
              control={
                <Checkbox 
                  checked={!!filters.inStock} 
                  onChange={handleCheckboxChange}
                  name="inStock"
                />
              } 
              label="In Stock Only" 
            />
            <FormControlLabel 
              control={
                <Checkbox 
                  checked={!!filters.onSale} 
                  onChange={handleCheckboxChange}
                  name="onSale"
                />
              } 
              label="On Sale Only" 
            />
          </FormGroup>
        </AccordionDetails>
      </Accordion>
      
      {/* Regular attributes */}
      {productFiltersData?.filters?.attributes && Object.entries(productFiltersData.filters.attributes)
        // Filter out numeric attributes that we handle separately
        .filter(([attributeName]) => !['Number of Servings', 'Price per Serving'].includes(attributeName))
        .map(([attributeName, values]) => (
          <Accordion 
            key={attributeName}
            expanded={expanded === `attr_${attributeName}`} 
            onChange={handleAccordionChange(`attr_${attributeName}`)}
            elevation={0}
            disableGutters
            sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              mb: 2,
              '&::before': { display: 'none' },
              '&.Mui-expanded': {
                margin: 0,
                mb: 2
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ minHeight: 48 }}
            >
              <Typography>{attributeName}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup>
                {values.map(value => (
                  <FormControlLabel 
                    key={value.id}
                    control={
                      <Checkbox 
                        checked={!!(
                          filters.attributes && 
                          filters.attributes[attributeName] && 
                          filters.attributes[attributeName].split(',').includes(value.id)
                        )} 
                        onChange={(e) => handleAttributeChange(
                          parseInt(attributeName), 
                          value.id, 
                          e.target.checked
                        )}
                      />
                    } 
                    label={
                      <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <span>{value.title}</span>
                        <Typography variant="caption" color="text.secondary">
                          ({value.productCount})
                        </Typography>
                      </Box>
                    } 
                  />
                ))}
              </FormGroup>
            </AccordionDetails>
          </Accordion>
        ))
      }
    </Box>
  );
  
  const MobileFilterButton = () => (
    <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
      <Button
        variant="outlined"
        startIcon={<TuneIcon />}
        onClick={toggleMobileFilters}
        fullWidth
      >
        Filters
      </Button>
    </Box>
  );
  
  const MobileFilterDrawer = () => (
    <Drawer
      anchor="bottom"
      open={mobileFiltersOpen}
      onClose={toggleMobileFilters}
      sx={{
        display: { xs: 'block', md: 'none' },
        '& .MuiDrawer-paper': {
          maxHeight: '80vh',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          p: 2
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Filters</Typography>
        <IconButton onClick={toggleMobileFilters}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ overflow: 'auto', pb: 2, height: 'calc(80vh - 60px)' }}>
        {renderFilters()}
      </Box>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Button 
          variant="outlined" 
          onClick={handleClearAll}
          sx={{ flex: 1, mr: 1 }}
        >
          Clear All
        </Button>
        <Button 
          variant="contained"
          onClick={toggleMobileFilters}
          sx={{ flex: 1, ml: 1 }}
        >
          Apply Filters
        </Button>
      </Box>
    </Drawer>
  );
  
  return (
    <>
      {renderFilters()}
    </>
  );
};

export default ProductFilters; 